import { useEffect, useState } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useModalState from '@/hooks/pure/useModalState';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getDiscordUrl } from '@/utils/integration';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { DiscordInfo } from '../types';
import DiscordWorkspaceChannelModal from './DiscordWorkspaceChannelModal';
import { useDiscord } from './useDiscord';

export const DiscordItem = (props: IntegrationItemProps) => {
  const { isConnector } = props;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const {
    discordWorkspaces,
    disconnectDiscordMutation,
    requestImportMutation,
  } = useDiscord(!isConnector);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<DiscordInfo | null>('discord-redirect', null);

  const channelModalState = useModalState<{
    workspace: DiscordInfo;
  }>();
  const importModalState = useModalState<{
    workspace: DiscordInfo;
    isNew: boolean;
  }>();

  const [firstConnectID, setFirstConnectID] = useState('');

  const handleConnect = () => {
    if (!currWorkspace) return;
    const url = getDiscordUrl(currWorkspace.alias);
    window.open(url);
  };

  const handleDisconnect = (info: DiscordInfo) => {
    disconnectDiscordMutation(info.alias);
  };

  const handleImportWorkspace = async (
    workspace: DiscordInfo,
    dateRange: NullableDateRangeT,
  ) => {
    requestImportMutation({ alias: workspace.alias, dateRange });
    importModalState.close();
  };

  // Handle redirect after OAuth
  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (redirect) {
      setRedirect(null);
      removeKey();
      // Open channel modal for newly connected workspace
      channelModalState.open({
        modalState: {
          workspace: discordWorkspaces.find((w) => w.alias === redirect.alias)!,
        },
      });
      setFirstConnectID(redirect.alias);
    }
  }, [redirect, removeKey, setRedirect, currWorkspace, isConnector]);

  // After channel modal closes, open import modal for first connection
  useEffect(() => {
    if (firstConnectID && !channelModalState.status) {
      const workspace = discordWorkspaces.find(
        (w) => w.alias === firstConnectID,
      );
      if (workspace) {
        importModalState.open({
          modalState: {
            workspace,
            isNew: true,
          },
        });
      }
      setFirstConnectID('');
    }
  }, [firstConnectID, channelModalState.status, discordWorkspaces]);

  if (isConnector && discordWorkspaces.length > 0) return null;

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={true}
        connected={discordWorkspaces.length > 0}
        source={SOURCE_TYPE.DISCORD}
        description="Import chats as feedback to Syncly."
        connectedDisplay={() => 'Discord workspace'}
        infos={discordWorkspaces}
        onDisconnect={handleDisconnect}
        onConnectClick={handleConnect}
        onAddInfoClick={handleConnect}
        additionalActions={(workspace: DiscordInfo) => (
          <div className="flex gap-2">
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                channelModalState.open({
                  modalState: { workspace },
                })
              }
            >
              Manage Channel
            </Button>
          </div>
        )}
      />
      <Modal
        status={channelModalState.status}
        closeModal={channelModalState.close}
      >
        {channelModalState.modalState?.workspace && (
          <DiscordWorkspaceChannelModal
            discordWorkspaceId={channelModalState.modalState.workspace.alias}
          />
        )}
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
      >
        {importModalState.modalState?.workspace && (
          <IntegrationImportModal
            isNew={importModalState.modalState.isNew}
            title={'Import Discord chats'}
            subTitle={'Discord'}
            historyList={importModalState.modalState.workspace.history || []}
            onSubmitImport={(start_date, end_date) =>
              handleImportWorkspace(importModalState.modalState.workspace, {
                start_date,
                end_date,
              })
            }
          />
        )}
      </Modal>
    </>
  );
};
